/**
 * WP-16: what the replay timeline needs from a recording, worked out once when it loads.
 *
 * - Jump markers: overlaps flagged, negotiation moves, failed verdicts and lands, each with the feed line it
 *   produced at the time.
 * - Checkpoints: the state every few hundred events, so scrubbing anywhere rebuilds from the nearest one
 *   instead of from the start. A checkpoint plus the events after it gives exactly the state a straight replay
 *   gives; the tests check that at every position.
 */
import type { PlatformEvent } from "@botallion/contracts";
import { describeEvent, type FeedLine, type ViewId } from "./describe.ts";
import { applyEvent, emptyState, type DashboardState } from "./state.ts";
import type { Store } from "./store.ts";

export type MarkerKind = "overlap" | "negotiation" | "verdict" | "land";

export const MARKER_KINDS: readonly MarkerKind[] = ["overlap", "negotiation", "verdict", "land"];

export const MARKER_LABEL: Record<MarkerKind, { one: string; many: string }> = {
  overlap: { one: "Overlap", many: "Overlaps" },
  negotiation: { one: "Negotiation", many: "Negotiations" },
  verdict: { one: "Failed verdict", many: "Failed verdicts" },
  land: { one: "Land", many: "Lands" },
};

export interface Marker {
  kind: MarkerKind;
  /** Index of the event in the recording. */
  index: number;
  seq: number;
  ts: string;
  /** The feed line the event produced, in plain English. */
  text: string;
  /** The tab the event belongs on. */
  view: ViewId;
}

/** The kind of jump marker an event is, or null. */
export function markerKind(e: PlatformEvent): MarkerKind | null {
  switch (e.type) {
    case "overlap.detected":
      return "overlap";
    case "message.posted":
      return e.data.message.overlapId && e.data.message.type !== "info" ? "negotiation" : null;
    case "resolution.recorded":
      return "negotiation";
    case "verdict.recorded":
      return e.data.verdict.pass ? null : "verdict";
    case "land.updated":
      return e.data.land.status === "merged" ? "land" : null;
    default:
      return null;
  }
}

interface Checkpoint {
  /** Events applied. */
  applied: number;
  state: DashboardState;
  feed: FeedLine[];
}

/**
 * Copy a state. Records inside it are never changed in place (every event replaces them), so copying the
 * containers is enough. The log is rebuilt from the recording on restore.
 */
function cloneState(s: DashboardState, log: PlatformEvent[]): DashboardState {
  return {
    ...s,
    sessions: { ...s.sessions },
    claims: { ...s.claims },
    overlaps: { ...s.overlaps },
    messages: [...s.messages],
    resolutions: [...s.resolutions],
    plans: { ...s.plans },
    packages: { ...s.packages },
    verdicts: [...s.verdicts],
    lands: { ...s.lands },
    deploys: { ...s.deploys },
    runs: { ...s.runs },
    diffs: [...s.diffs],
    claimLeaseMs: { ...s.claimLeaseMs },
    log,
  };
}

export class Recording {
  readonly events: readonly PlatformEvent[];
  readonly markers: readonly Marker[];
  private readonly checkpoints: Checkpoint[] = [];
  private readonly every: number;

  constructor(events: readonly PlatformEvent[], opts: { checkpointEvery?: number } = {}) {
    this.events = events;
    this.every = Math.max(1, opts.checkpointEvery ?? 250);
    const markers: Marker[] = [];
    const state = emptyState();
    const feed: FeedLine[] = [];
    this.checkpoints.push({ applied: 0, state: cloneState(state, []), feed: [] });
    events.forEach((e, i) => {
      applyEvent(state, e);
      const line = describeEvent(state, e);
      if (line) feed.push(line);
      const kind = markerKind(e);
      if (kind && line) markers.push({ kind, index: i, seq: e.seq, ts: e.ts, text: line.text, view: line.view });
      if ((i + 1) % this.every === 0) this.checkpoints.push({ applied: i + 1, state: cloneState(state, []), feed: [...feed] });
    });
    this.markers = markers;
  }

  markersOf(kind: MarkerKind): Marker[] {
    return this.markers.filter((m) => m.kind === kind);
  }

  /** Put the store in exactly the state after the first `applied` events. */
  restore(store: Store, applied: number): void {
    const n = Math.max(0, Math.min(this.events.length, applied));
    let cp = this.checkpoints[0]!;
    for (const c of this.checkpoints) if (c.applied <= n) cp = c;
    store.load(cloneState(cp.state, this.events.slice(0, cp.applied) as PlatformEvent[]), [...cp.feed], this.events.slice(cp.applied, n));
  }
}
